import { components } from "@/api-schema";
import {
  PathsV1BranchSearchPostParametersQueryExtend,
  PathsV1BranchSearchPostParametersQuerySelect,
  PathsV1BranchUuidGetParametersQueryExtend,
  PathsV1BrandSearchPostParametersQuerySelect,
  PathsV1CompanySearchPostParametersQuerySelect,
} from "@/api-schema-enums";
import { Can } from "@/casl-provider";
import { useBranchByUuidQuery } from "@/common/api/branch/use-branch-by-uuid.query";
import { useBranchSearchQuery } from "@/common/api/branch/use-branch-search.query";
import { useBranchUpdateMutation } from "@/common/api/branch/use-branch-update.mutation";
import { useBrandSearchQuery } from "@/common/api/brand/use-brand-search.query";
import { useCompanySearchQuery } from "@/common/api/company/use-company-search.query";
import { PHONE_REGEX } from "@/common/helpers/regex";
import { toastMutationStatus } from "@/common/helpers/toast-mutation-status";
import { AddressFormFields } from "@/components/address-form-fields/address-form-fields";
import { addressFormDefaultValues, addressFormSchema } from "@/components/address-form-fields/address-form-schema";
import { Button, ButtonColorVariant, ButtonVariant } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { FormCardActions } from "@/components/ui/form-card";
import { Input } from "@/components/ui/input";
import { MultiSelect } from "@/components/ui/multi-select";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { SystemInfoFields, SystemInfoIdentifierType } from "@/components/system-info-fields/system-info-fields";
import {
  BranchDunningFormCard,
  branchDunningFormSchemaShape,
} from "@/modules/masterdata/branch/_components/branch-dunning-form-card";
import { BranchIdentifierFormCard } from "@/modules/masterdata/branch/_components/branch-identifier-form-card";
import { BrandRelationsFields } from "@/modules/masterdata/branch/_components/brand-relations-fields";
import { serializeBranchClipboardData, toBranchClipboardData } from "@/modules/masterdata/branch/branch-clipboard";
import {
  branchIdentifierFormDefaultValues,
  branchIdentifierFormSchema,
  getDuplicateBranchIdentifierKeys,
  toBranchIdentifierDtos,
  toBranchIdentifierFormValues,
} from "@/modules/masterdata/branch/branch-identifiers";
import { zodResolver } from "@hookform/resolvers/zod";
import { Building2, Copy } from "lucide-react";
import { useCallback, useEffect, useMemo } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useLocation } from "wouter";
import { z } from "zod";

type AddressInput = components["schemas"]["AddressDto"] & { city?: string | null };
type Company = components["schemas"]["Company"];

export const BranchUpdateFormSchema = z.object({
  branchIdentifiers: branchIdentifierFormSchema,
  name: z.string().trim().min(1, "masterdata:BranchNameIsRequired"),
  phone: z.string().regex(PHONE_REGEX, "masterdata:PhoneMustBeValid").or(z.literal("")).optional(),
  email: z.email("masterdata:EmailMustBeValid").or(z.literal("")).optional(),
  ...branchDunningFormSchemaShape,
  signatureTabletEmail: z.email("masterdata:EmailMustBeValid").or(z.literal("")).optional(),
  companyUuid: z.string().min(1, "masterdata:CompanyIsRequired"),
  address: addressFormSchema,
  brands: z.array(z.string()).optional(),
  brandRelations: z
    .array(
      z.object({
        brandUuid: z.string(),
        phone: z.string().regex(PHONE_REGEX, "masterdata:PhoneMustBeValid").or(z.literal("")).optional(),
        fax: z.string().regex(PHONE_REGEX, "masterdata:PhoneMustBeValid").or(z.literal("")).optional(),
        email: z.email("masterdata:EmailMustBeValid").or(z.literal("")).optional(),
      }),
    )
    .optional(),
});

export type BranchUpdateFormSchemaType = z.infer<typeof BranchUpdateFormSchema>;

interface BranchUpdateFormFieldsProps {
  uuid: string;
}

export function BranchUpdateFormFields({ uuid }: BranchUpdateFormFieldsProps) {
  const { t } = useTranslation(["masterdata", "common"]);
  const [, navigate] = useLocation();
  const { mutateAsync, isPending } = useBranchUpdateMutation();
  const { data: branchData, isPending: isBranchLoading } = useBranchByUuidQuery(uuid, {
    parameters: {
      extend: [
        PathsV1BranchUuidGetParametersQueryExtend.Address,
        PathsV1BranchUuidGetParametersQueryExtend.Company,
        PathsV1BranchUuidGetParametersQueryExtend.Brands,
        PathsV1BranchUuidGetParametersQueryExtend.BranchIdentifiers,
        PathsV1BranchUuidGetParametersQueryExtend.BrandRelations,
        PathsV1BranchUuidGetParametersQueryExtend.BrandRelations_Brand,
        PathsV1BranchUuidGetParametersQueryExtend.DunningUser,
        PathsV1BranchUuidGetParametersQueryExtend.DunningRepresentative,
      ],
    },
  });

  const form = useForm<BranchUpdateFormSchemaType>({
    resolver: zodResolver(BranchUpdateFormSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      branchIdentifiers: branchIdentifierFormDefaultValues,
      name: "",
      phone: "",
      email: "",
      dunningUser: "",
      dunningRepresentative: "",
      accountingDunningClerkId: "",
      signatureTabletEmail: "",
      companyUuid: "",
      brands: [],
      brandRelations: [],
      address: addressFormDefaultValues,
    },
  });

  const watchedBranchIdentifiers = useWatch({ control: form.control, name: "branchIdentifiers" });
  const watchedCompanyUuid = useWatch({ control: form.control, name: "companyUuid" });

  const { data: companies, isPending: isCompaniesLoading } = useCompanySearchQuery({
    options: {
      body: {},
      parameters: {
        pageSize: -1,
        select: [
          PathsV1CompanySearchPostParametersQuerySelect.Uuid,
          PathsV1CompanySearchPostParametersQuerySelect.Name,
        ],
      },
    },
  });

  const { data: brands, isPending: isBrandsLoading } = useBrandSearchQuery({
    options: {
      body: {},
      parameters: {
        pageSize: -1,
        select: [
          PathsV1BrandSearchPostParametersQuerySelect.Uuid,
          PathsV1BrandSearchPostParametersQuerySelect.Name,
          PathsV1BrandSearchPostParametersQuerySelect.LogoPath,
        ],
      },
    },
  });

  const { data: existingBranches, isPending: isExistingBranchesLoading } = useBranchSearchQuery({
    options: {
      body: {},
      parameters: {
        pageSize: -1,
        extend: [PathsV1BranchSearchPostParametersQueryExtend.BranchIdentifiers],
        select: [
          PathsV1BranchSearchPostParametersQuerySelect.Uuid,
          PathsV1BranchSearchPostParametersQuerySelect.BranchIdentifiers,
        ],
      },
    },
  });

  const companyOptions = useMemo(() => {
    const items: Company[] = companies?.data ? [...companies.data] : [];
    const currentCompany = branchData?.company;

    if (currentCompany && !items.some((company) => company.uuid === currentCompany.uuid)) {
      items.push(currentCompany);
    }

    return items;
  }, [branchData?.company, companies]);

  useEffect(() => {
    if (!branchData?.company?.uuid) {
      return;
    }

    if (!watchedCompanyUuid) {
      form.setValue("companyUuid", branchData.company.uuid, { shouldDirty: false, shouldValidate: false });
    }
  }, [branchData?.company?.uuid, form, watchedCompanyUuid]);

  useEffect(() => {
    if (!branchData) {
      return;
    }

    const companyUuid = branchData.company?.uuid;

    if (!companyUuid) {
      return;
    }

    const address = (branchData.address ?? {}) as AddressInput;

    form.reset(
      {
        branchIdentifiers: toBranchIdentifierFormValues(branchData.branchIdentifiers),
        name: branchData.name ?? "",
        phone: branchData.phone ?? "",
        email: branchData.email ?? "",
        dunningUser: branchData.dunningUser?.uuid ?? "",
        dunningRepresentative: branchData.dunningRepresentative?.uuid ?? "",
        accountingDunningClerkId: branchData.accountingDunningClerkId ?? "",
        signatureTabletEmail: branchData.signatureTabletEmail ?? "",
        companyUuid: companyUuid,
        brands: branchData.brands?.map((b) => b.uuid) ?? [],
        brandRelations:
          branchData.brandRelations?.map((contact) => ({
            brandUuid: contact.brand?.uuid ?? "",
            phone: contact.phone ?? "",
            fax: contact.fax ?? "",
            email: contact.email ?? "",
          })) ?? [],
        address: {
          recipientName: address.recipientName ?? "",
          company: address.company ?? "",
          street: address.street ?? "",
          houseNumber: address.houseNumber ?? "",
          houseExtension: address.houseExtension ?? "",
          postalCode: address.postalCode ?? "",
          city: address.city ?? "",
          country_code: address.country_code ?? "",
          latitude: address.latitude !== null && address.latitude !== undefined ? String(address.latitude) : "",
          longitude: address.longitude !== null && address.longitude !== undefined ? String(address.longitude) : "",
        },
      },
      {
        keepDefaultValues: false,
      },
    );
    form.setValue("companyUuid", companyUuid, { shouldDirty: false, shouldValidate: false });
  }, [branchData, form]);

  const onSubmit = async (values: BranchUpdateFormSchemaType) => {
    const latitude = values.address.latitude?.trim();
    const longitude = values.address.longitude?.trim();
    const latitudeNumber = latitude && !Number.isNaN(Number(latitude)) ? Number(latitude) : null;
    const longitudeNumber = longitude && !Number.isNaN(Number(longitude)) ? Number(longitude) : null;

    const address: AddressInput = {
      recipientName: values.address.recipientName?.trim() || null,
      company: values.address.company?.trim() || null,
      street: values.address.street.trim(),
      houseNumber: values.address.houseNumber.trim(),
      houseExtension: values.address.houseExtension?.trim() || null,
      postalCode: values.address.postalCode.trim(),
      city: values.address.city.trim(),
      country_code: values.address.country_code.trim(),
      latitude: latitudeNumber,
      longitude: longitudeNumber,
    };

    const brandRelations = (values.brandRelations ?? [])
      .filter((contact) => contact.brandUuid)
      .map((contact) => ({
        brandUuid: contact.brandUuid,
        phone: contact.phone?.trim() || null,
        fax: contact.fax?.trim() || null,
        email: contact.email?.trim() || null,
      }));

    try {
      await toastMutationStatus({
        mutationFn: () =>
          mutateAsync({
            uuid,
            branchIdentifiers: toBranchIdentifierDtos(values.branchIdentifiers),
            name: values.name.trim(),
            phone: values.phone?.trim() || null,
            email: values.email?.trim() || null,
            dunningUser: values.dunningUser || null,
            dunningRepresentative: values.dunningRepresentative || null,
            accountingDunningClerkId: values.accountingDunningClerkId?.trim() || null,
            signatureTabletEmail: values.signatureTabletEmail?.trim() || null,
            address,
            company: values.companyUuid,
            brandRelations,
          }),
        successMessage: t("masterdata:BranchSuccessfullyUpdated"),
        errorMessage: t("masterdata:ErrorUpdatingBranch"),
        customErrors: { 409: t("masterdata:BranchIdentifierConflict") },
      });

      navigate("~/masterdata/branch");
    } catch (error) {
      console.error("Error updating branch:", error);
    }
  };

  const duplicateValidation = useMemo(() => {
    const duplicateKeys = getDuplicateBranchIdentifierKeys(
      existingBranches?.data,
      watchedBranchIdentifiers ?? branchIdentifierFormDefaultValues,
      uuid,
    );

    return {
      duplicateKeys,
      hasErrors: duplicateKeys.size > 0,
      errors: duplicateKeys.size > 0 ? [t("masterdata:BranchIdentifierConflict")] : [],
    };
  }, [existingBranches?.data, t, uuid, watchedBranchIdentifiers]);

  const handleSubmitWithValidation = (values: BranchUpdateFormSchemaType) => {
    if (duplicateValidation.hasErrors) {
      duplicateValidation.errors.forEach((error) => {
        toast.error(t("masterdata:ValidationError"), {
          description: error,
          duration: 5000,
        });
      });
      return;
    }
    onSubmit(values);
  };

  const handleCopyAllValues = useCallback(async () => {
    const values = form.getValues();

    try {
      await navigator.clipboard.writeText(serializeBranchClipboardData(toBranchClipboardData(values)));
      toast.success(t("masterdata:BranchDataCopiedToClipboard"));
    } catch {
      toast.error(t("common:ErrorCopyingToClipboard"));
    }
  }, [form, t]);

  const isLoading = isBranchLoading || isCompaniesLoading || isExistingBranchesLoading || isBrandsLoading;

  if (isLoading && !branchData) {
    return (
      <div className="space-y-6">
        <div className="space-y-6">
          <div className="space-y-2">
            <Skeleton className="h-4 w-12" />
            <Skeleton className="h-10 w-full rounded-md" />
          </div>

          <div className="space-y-2">
            <Skeleton className="h-4 w-12" />
            <Skeleton className="h-10 w-full rounded-md" />
          </div>

          <div className="space-y-2">
            <Skeleton className="h-4 w-12" />
            <Skeleton className="h-10 w-full rounded-md" />
          </div>

          <div className="space-y-2">
            <Skeleton className="h-4 w-12" />
            <Skeleton className="h-10 w-full rounded-md" />
          </div>

          <div className="space-y-2">
            <Skeleton className="h-4 w-12" />
            <Skeleton className="h-10 w-full rounded-md" />
          </div>

          <div className="space-y-2">
            <Skeleton className="h-4 w-12" />
            <Skeleton className="h-10 w-full rounded-md" />
          </div>

          <div className="space-y-2">
            <Skeleton className="h-4 w-12" />
            <Skeleton className="h-10 w-full rounded-md" />
          </div>

          <div className="space-y-2">
            <Skeleton className="h-4 w-12" />
            <Skeleton className="h-10 w-full rounded-md" />
          </div>

          <div className="space-y-2">
            <Skeleton className="h-4 w-12" />
            <Skeleton className="h-10 w-full rounded-md" />
          </div>

          <div className="space-y-2">
            <Skeleton className="h-4 w-12" />
            <Skeleton className="h-10 w-full rounded-md" />
          </div>

          <div className="space-y-2">
            <Skeleton className="h-4 w-12" />
            <Skeleton className="h-10 w-full rounded-md" />
          </div>

          <div className="space-y-2">
            <Skeleton className="h-4 w-12" />
            <Skeleton className="h-10 w-full rounded-md" />
          </div>

          <div className="flex justify-end space-x-4">
            <Skeleton className="h-10 w-24 rounded-md" />
            <Skeleton className="h-10 w-24 rounded-md" />
          </div>
        </div>
      </div>
    );
  }

  const isSubmitDisabled =
    isPending ||
    isExistingBranchesLoading ||
    duplicateValidation.hasErrors ||
    !form.formState.isValid ||
    isBrandsLoading;

  return (
    <Form {...form}>
      <form className="space-y-6" onSubmit={form.handleSubmit(handleSubmitWithValidation)}>
        <div className="space-y-6">
          <Card className="shadow-none">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                {t("common:BasicInformation")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("common:Name")} *</FormLabel>
                    <FormControl>
                      <Input placeholder={t("masterdata:BranchNameExample")} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("masterdata:BranchPhone")}</FormLabel>
                      <FormControl>
                        <Input placeholder={t("masterdata:BranchPhoneExample")} {...field} value={field.value ?? ""} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("masterdata:BranchEmail")}</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder={t("masterdata:BranchEmailExample")}
                          {...field}
                          value={field.value ?? ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="signatureTabletEmail"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("masterdata:BranchSignatureTabletEmail")}</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder={t("masterdata:BranchSignatureTabletEmailExample")}
                        {...field}
                        value={field.value ?? ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="companyUuid"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("common:Company")} *</FormLabel>
                    <Select
                      value={field.value || branchData?.company?.uuid || ""}
                      onValueChange={field.onChange}
                      disabled={isCompaniesLoading}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue
                            placeholder={
                              isCompaniesLoading ? t("masterdata:CompaniesAreLoading") : t("masterdata:SelectCompany")
                            }
                          />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {companyOptions.map((company) => (
                          <SelectItem key={company.uuid} value={company.uuid}>
                            {company.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="brands"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("masterdata:Brands")}</FormLabel>
                    <FormControl>
                      <MultiSelect
                        options={
                          brands?.data?.map((brand) => ({
                            label: brand.name,
                            value: brand.uuid,
                          })) || []
                        }
                        onValueChange={field.onChange}
                        defaultValue={field.value || []}
                        placeholder={isBrandsLoading ? t("masterdata:BrandsAreLoading") : t("ram:SelectBrands")}
                        maxCount="auto"
                        disabled={isBrandsLoading}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <BrandRelationsFields brands={brands?.data ?? []} />
            </CardContent>
          </Card>

          <SystemInfoFields
            identifiers={
              branchData?.technicalId
                ? [
                    {
                      value: branchData.technicalId,
                      label: { kind: "known", type: SystemInfoIdentifierType.TechnicalId },
                    },
                  ]
                : undefined
            }
            uuid={branchData?.uuid}
            createdAt={branchData?.createdAt}
            updatedAt={branchData?.updatedAt}
            orientation="horizontal"
          />
        </div>

        <BranchDunningFormCard />

        <BranchIdentifierFormCard
          duplicateKeys={duplicateValidation.duplicateKeys}
          isCheckingDuplicates={isExistingBranchesLoading}
        />

        <AddressFormFields />

        <Can I="manage" a="Branch">
          <div className="flex justify-end">
            <Button
              type="button"
              variant={ButtonVariant.Outlined}
              colorVariant={ButtonColorVariant.Secondary}
              onClick={handleCopyAllValues}
            >
              <Copy className="mr-2 h-4 w-4" />
              {t("masterdata:CopyAllValues")}
            </Button>
          </div>
        </Can>

        <FormCardActions
          cancelLabel={t("common:Cancel")}
          onCancel={() => navigate("~/masterdata/branch")}
          submitDisabled={isSubmitDisabled}
          submitLabel={t("common:SaveChanges")}
          isPending={isPending}
        />
      </form>
    </Form>
  );
}
